import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import UploadResearchModal from "../../components/UploadResearchModal";
import ProcessRequestModal from "../../components/ProcessRequestModal";
import { formatRequestTimestamp } from "../../utils/formatDateTime";
import ViewResearchModal from "../../components/ViewResearchModal";
import EditResearchModal from "../../components/EditResearchModal";

export default function CoordinatorDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({});
  const [research, setResearch] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadResearchModal, setShowUploadResearchModal] = useState(false);
  const [showProcessRequestModal, setShowProcessRequestModal] = useState(false);
  const [showViewResearchModal, setShowViewResearchModal] = useState(false);
  const [showEditResearchModal, setShowEditResearchModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedResearch, setSelectedResearch] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      setUser(JSON.parse(userData));
    }
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, researchRes, requestsRes] = await Promise.all([
        api.get("/analytics/summary"),
        api.get("/research"),
        api.get("/request")
      ]);
      
      setStats(statsRes.data);
      setResearch(researchRes.data.research);
      setRequests(requestsRes.data.requests);
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const handleResearchAdded = () => {
    loadDashboardData();
  };

  const handleRequestProcessed = () => {
    loadDashboardData();
  };

  const handleProcessRequest = (request) => {
    setSelectedRequest(request);
    setShowProcessRequestModal(true);
  };

  const handleViewResearch = (research) => {
    setSelectedResearch(research);
    setShowViewResearchModal(true);
  };

  const handleEditResearch = (research) => {
    setSelectedResearch(research);
    setShowEditResearchModal(true);
  };

  const handleResearchUpdated = () => {
    loadDashboardData();
  };

  const renderOverview = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <div className="bg-blue-800 p-4 rounded-lg">
        <h3 className="text-lg font-semibold">Campus Research</h3>
        <p className="text-2xl font-bold">{stats.summary?.totalResearch || 0}</p>
      </div>
      <div className="bg-yellow-800 p-4 rounded-lg">
        <h3 className="text-lg font-semibold">Total Requests</h3>
        <p className="text-2xl font-bold">{stats.summary?.totalRequests || 0}</p>
      </div>
      <div className="bg-red-800 p-4 rounded-lg">
        <h3 className="text-lg font-semibold">Pending Requests</h3>
        <p className="text-2xl font-bold">{stats.summary?.pendingRequests || 0}</p>
      </div>
      <div className="bg-green-800 p-4 rounded-lg">
        <h3 className="text-lg font-semibold">Approved Requests</h3>
        <p className="text-2xl font-bold">
          {requests.filter(r => r.status === "approved").length}
        </p>
      </div>
    </div>
  );

  const renderResearchManagement = () => (
    <div className="bg-gray-800 p-6 rounded-lg">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold">Research Documents ({user?.campus})</h3>
        <button 
          onClick={() => setShowUploadResearchModal(true)}
          className="bg-green-600 px-4 py-2 rounded hover:bg-green-700"
        >
          Upload Research
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {research
          .filter(doc => doc.campus === user?.campus)
          .map((doc) => (
          <div key={doc._id} className="bg-gray-700 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">{doc.title}</h4>
            <p className="text-sm text-gray-300 mb-2">
              Authors: {doc.authors.join(", ")}
            </p>
            <p className="text-sm text-gray-300 mb-2">
              Year: {doc.year}
            </p>
            <p className="text-sm text-gray-300 mb-2">
              Status: <span className={`px-2 py-1 rounded text-xs ${
                doc.status === 'approved' ? 'bg-green-600' :
                doc.status === 'pending' ? 'bg-yellow-600' : 'bg-red-600'
              }`}>
                {doc.status}
              </span>
            </p>
            <div className="flex gap-2">
              <button 
                onClick={() => handleViewResearch(doc)}
                className="bg-blue-600 px-2 py-1 rounded text-xs hover:bg-blue-700"
              >
                View
              </button>
              <button 
                onClick={() => handleEditResearch(doc)}
                className="bg-green-600 px-2 py-1 rounded text-xs hover:bg-green-700"
              >
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAccessRequests = () => (
    <div className="bg-gray-800 p-6 rounded-lg">
      <h3 className="text-xl font-semibold mb-4">Access Requests ({user?.campus})</h3>
      <div className="space-y-4">
        {requests
          .filter(request => request.researchId?.campus === user?.campus)
          .map((request) => (
          <div key={request._id} className="bg-gray-700 p-4 rounded-lg">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-semibold">{request.studentId?.name}</p>
                <p className="text-sm text-gray-300">
                  Requesting: {request.researchId?.title}
                </p>
                <p className="text-sm text-gray-300">
                  Reason: {request.reason}
                </p>
                <p className="text-sm text-gray-300">
                  <span className="text-gray-500">Requested:</span>{" "}
                  {formatRequestTimestamp(request.dateRequested)}
                </p>
                {request.dateProcessed && (
                  <p className="text-sm text-gray-300">
                    <span className="text-gray-500">Processed:</span>{" "}
                    {formatRequestTimestamp(request.dateProcessed)}
                  </p>
                )}
                <p className="text-sm text-gray-300">
                  Status: <span className={`px-2 py-1 rounded text-xs ${
                    request.status === 'approved' ? 'bg-green-600' :
                    request.status === 'declined' ? 'bg-red-600' : 'bg-yellow-600'
                  }`}>
                    {request.status}
                  </span>
                </p>
              </div>
              {request.status === "pending" && (
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleProcessRequest(request)}
                    className="bg-green-600 px-3 py-1 rounded text-sm"
                  >
                    Process Request
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className="bg-gray-800 p-6 rounded-lg">
      <h3 className="text-xl font-semibold mb-4">Campus Analytics</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-semibold mb-2">Research by Year</h4>
          {research
            .filter(doc => doc.campus === user?.campus)
            .reduce((acc, doc) => {
              acc[doc.year] = (acc[doc.year] || 0) + 1;
              return acc;
            }, {})
            && Object.entries(
              research
                .filter(doc => doc.campus === user?.campus)
                .reduce((acc, doc) => {
                  acc[doc.year] = (acc[doc.year] || 0) + 1;
                  return acc;
                }, {})
            )
            .sort(([a], [b]) => b - a)
            .map(([year, count]) => (
              <div key={year} className="flex justify-between mb-2">
                <span>{year}</span>
                <span className="font-semibold">{count}</span>
              </div>
            ))}
        </div>
        <div>
          <h4 className="font-semibold mb-2">Request Status</h4>
          {requests
            .filter(request => request.researchId?.campus === user?.campus)
            .reduce((acc, request) => {
              acc[request.status] = (acc[request.status] || 0) + 1;
              return acc;
            }, {})
            && Object.entries(
              requests
                .filter(request => request.researchId?.campus === user?.campus)
                .reduce((acc, request) => {
                  acc[request.status] = (acc[request.status] || 0) + 1;
                  return acc;
                }, {})
            )
            .map(([status, count]) => (
              <div key={status} className="flex justify-between mb-2">
                <span className="capitalize">{status}</span>
                <span className="font-semibold">{count}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-950">
      {/* Header */}
      <header className="bg-green-900 p-4">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">
            <span className="font-extrabold">SKSU Digital Research Hub</span> &amp; Open Access Journal - Coordinator Dashboard
          </h1>
          <div className="flex items-center gap-4">
            <span>Welcome, {user?.name} ({user?.campus})</span>
            <button
              onClick={handleLogout}
              className="bg-red-600 px-4 py-2 rounded hover:bg-red-700"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-green-800 p-4">
        <div className="flex gap-4">
          {[
            { id: "overview", label: "Overview" },
            { id: "research", label: "Research Management" },
            { id: "requests", label: "Access Requests" },
            { id: "analytics", label: "Analytics" }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded ${
                activeTab === tab.id
                  ? "bg-green-600 text-white"
                  : "bg-green-700 text-green-100 hover:bg-green-600"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="p-6">
        {activeTab === "overview" && renderOverview()}
        {activeTab === "research" && renderResearchManagement()}
        {activeTab === "requests" && renderAccessRequests()}
        {activeTab === "analytics" && renderAnalytics()}
      </main>

      {/* Modals */}
      <UploadResearchModal
        isOpen={showUploadResearchModal}
        onClose={() => setShowUploadResearchModal(false)}
        onResearchAdded={handleResearchAdded}
      />

      <ProcessRequestModal
        isOpen={showProcessRequestModal}
        onClose={() => setShowProcessRequestModal(false)}
        onRequestProcessed={handleRequestProcessed}
        request={selectedRequest}
      />

      <ViewResearchModal
        isOpen={showViewResearchModal}
        onClose={() => setShowViewResearchModal(false)}
        research={selectedResearch}
      />

      <EditResearchModal
        isOpen={showEditResearchModal}
        onClose={() => setShowEditResearchModal(false)}
        research={selectedResearch}
        onResearchUpdated={handleResearchUpdated}
      />
    </div>
  );
} 